import React, { useState } from "react";
import { motion } from "framer-motion";
import { Clock, DollarSign, Timer } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface TimeSummaryReportProps {
  data: {
    byLawyer: {
      userId: string;
      name: string;
      totalMinutes: number;
      billableMinutes: number;
      totalAmountCents: number;
    }[];
    byMatter: {
      matterId: string;
      title: string;
      totalMinutes: number;
      totalAmountCents: number;
    }[];
  } | null;
  loading: boolean;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const formatHours = (minutes: number) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const TimeSummaryReport: React.FC<TimeSummaryReportProps> = ({ data, loading }) => {
  const [showBillableOnly, setShowBillableOnly] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-32 animate-pulse">
            <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
            <div className="h-8 w-24 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const totalMinutes = data.byLawyer.reduce((s, l) => s + l.totalMinutes, 0);
  const billableMinutes = data.byLawyer.reduce((s, l) => s + l.billableMinutes, 0);
  const totalValue = data.byLawyer.reduce((s, l) => s + l.totalAmountCents, 0);

  const summaryCards = [
    {
      label: "Total Hours",
      value: formatHours(totalMinutes),
      icon: <Clock className="h-5 w-5" />,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Billable Hours",
      value: formatHours(billableMinutes),
      icon: <Timer className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Total Value",
      value: formatCurrency(totalValue),
      icon: <DollarSign className="h-5 w-5" />,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  const lawyerChartData = data.byLawyer.map((l) => ({
    name: l.name.length > 20 ? l.name.substring(0, 20) + "..." : l.name,
    hours: (showBillableOnly ? l.billableMinutes : l.totalMinutes) / 60,
    fullName: l.name,
  }));

  const matterChartData = data.byMatter.slice(0, 10).map((m) => ({
    name: m.title.length > 25 ? m.title.substring(0, 25) + "..." : m.title,
    hours: m.totalMinutes / 60,
    fullTitle: m.title,
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowBillableOnly(!showBillableOnly)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: showBillableOnly ? "var(--color-primary)" : "#f1f5f9",
            color: showBillableOnly ? "#ffffff" : "#475569",
          }}
        >
          <Timer className="h-4 w-4" />
          {showBillableOnly ? "Showing Billable Only" : "Show Billable Only"}
        </button>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours by Lawyer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Hours by Lawyer</h3>
          {lawyerChartData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Clock className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No time data</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Time tracking summaries will appear here as your team logs billable hours.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, lawyerChartData.length * 45)}>
              <BarChart data={lawyerChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  tickFormatter={(v) => `${v.toFixed(1)}h`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  width={120}
                />
                <Tooltip
                  formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toFixed(1)} hours`, showBillableOnly ? "Billable" : "Total"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar dataKey="hours" fill="var(--color-primary)" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Hours by Matter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            Hours by Matter {data.byMatter.length > 10 && "(Top 10)"}
          </h3>
          {matterChartData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Clock className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No time data</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Time tracking summaries will appear here as your team logs billable hours.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, matterChartData.length * 45)}>
              <BarChart data={matterChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  tickFormatter={(v) => `${v.toFixed(1)}h`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  width={150}
                />
                <Tooltip
                  formatter={(value: number | string | undefined) => [`${Number(value ?? 0).toFixed(1)} hours`, "Total"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar dataKey="hours" fill="var(--color-primary-hover)" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TimeSummaryReport;
